import React, { useRef, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import apiService, { getImageUrl } from "../../services/api";

// Updated at 01:07 - TourGuide is now dynamic!

// Fallback guides if API fails
const fallbackGuides = [
  { id: 1, name: "Jacob Jones", image: "/assets/img/team/team_1_1.jpg", designation: "Tourist Guide", rating: 4.8, experience: 5 },
  { id: 2, name: "Jane Cooper", image: "/assets/img/team/team_1_2.jpg", designation: "Tourist Guide", rating: 4.7, experience: 3 },
  { id: 3, name: "Guy Hawkins", image: "/assets/img/team/team_1_3.jpg", designation: "Tourist Guide", rating: 4.9, experience: 8 },
  { id: 4, name: "Jenny Wilson", image: "/assets/img/team/team_1_4.jpg", designation: "Tourist Guide", rating: 4.6, experience: 4 },
];

function TourGuide() {
  const [swiperInstance, setSwiperInstance] = useState(null);
  const paginationRef = useRef(null);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch tour guides from API
  useEffect(() => {
    const fetchTourGuides = async () => {
      try {
        setLoading(true);
        console.log('TourGuide: Fetching tour guides...');
        
        const response = await apiService.getTourGuides();
        console.log('TourGuide: API response:', response);
        
        // Map API data to component format
        const mappedGuides = response.results?.map(guide => ({
          id: guide.id,
          name: guide.full_name,
          image: getImageUrl(guide.profile_image) || '/assets/img/team/team_1_1.jpg',
          designation: 'Tourist Guide',
          rating: parseFloat(guide.rating) || 0,
          experience: guide.experience_years || 0,
          slug: guide.slug,
          hourly_rate: parseFloat(guide.hourly_rate) || 0,
          daily_rate: parseFloat(guide.daily_rate) || 0,
          review_count: guide.review_count || 0
        })) || [];
        
        console.log('TourGuide: Mapped guides:', mappedGuides);
        
        // Use API data if available, otherwise fallback
        setGuides(mappedGuides.length > 0 ? mappedGuides : fallbackGuides);
        setError(null);
        
      } catch (error) {
        console.error('TourGuide: Error fetching tour guides:', error);
        setError(error.message);
        
        // Use fallback data if API fails
        setGuides(fallbackGuides);
      } finally {
        setLoading(false);
      }
    };

    fetchTourGuides();
  }, []);

  // Swiper pagination effect
  useEffect(() => {
    if (swiperInstance && paginationRef.current) {
      swiperInstance.params.pagination.el = paginationRef.current;
      swiperInstance.pagination.init();
      swiperInstance.pagination.update();
    }
  }, [swiperInstance]);

  return (
    <section
      className="bg-smoke space overflow-hidden"
      style={{ backgroundImage: "url(/assets/img/bg/team_bg_1.png)" }}
    >
      <div className="container z-index-common">
        <div className="title-area text-center">
          <span className="sub-title">Meet with Guide</span>
          <h2 className="sec-title">Tour Guide</h2>
        </div>
        {loading ? (
          <div className="text-center">
            <h3>Loading Tour Guides...</h3>
          </div>
        ) : (
          <div className="slider-area">
            {guides.length > 0 ? (
              <Swiper
                modules={[Pagination, Autoplay]}
                slidesPerView={1}
                spaceBetween={20}
                speed={1200}
                breakpoints={{
                  576: { slidesPerView: 1 },
                  768: { slidesPerView: 2 },
                  992: { slidesPerView: 3 },
                  1200: { slidesPerView: 4 },
                }}
                pagination={{ clickable: true }}
                autoplay={{ delay: 3000 }}
                className="th-slider teamSlider1 has-shadow"
                onSwiper={setSwiperInstance} // Save Swiper instance
              >
                {guides.map((guide) => (
                  <SwiperSlide key={guide.id}>
                    <div className="th-team team-box">
                      <div className="team-img">
                        <img 
                          src={guide.image} 
                          alt={guide.name}
                          onError={(e) => {
                            e.target.src = '/assets/img/team/team_1_1.jpg'; // fallback
                          }}
                        />
                      </div>
                      <div className="team-content">
                        <div className="media-body">
                          <h3 className="box-title">
                            <Link to={`/tour-guide/${guide.slug || guide.id}`}>{guide.name}</Link>
                          </h3>
                          <span className="team-desig">{guide.designation}</span>
                          
                          {/* Show experience and rating if available */}
                          {guide.experience > 0 && (
                            <div className="team-info mt-2">
                              <small className="text-muted d-block">
                                <i className="fa-light fa-calendar" /> {guide.experience} years experience
                              </small>
                              {guide.rating > 0 && (
                                <small className="text-muted d-block">
                                  <i className="fa-light fa-star" /> {guide.rating.toFixed(1)} ({guide.review_count} reviews)
                                </small>
                              )}
                            </div>
                          )}
                          
                          <div className="th-social">
                            {["facebook", "twitter", "instagram", "linkedin"].map((platform) => (
                              <Link key={platform} target="_blank" rel="noopener noreferrer" to={`https://${platform}.com/`}>
                                <i className={`fab fa-${platform}`} />
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className="text-center">
                <h4>No tour guides available</h4>
                <p>Please add some tour guides in the admin panel.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default TourGuide;
